import { inject } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import {
  AssistantApiService,
  GoalApiService,
  GoalInterface,
  ListAssistantRequest,
  ListAssistantRequestFilters,
  ListGoalsRequestSortingOrder,
  Namespace,
} from '@vendasta/ai-assistants';
import { AvatarGroupItem } from '@vendasta/galaxy/avatar';
import {
  GalaxyDataSource,
  PagedListRequestInterface,
  PagedResponseInterface,
  PaginatedAPIInterface,
} from '@vendasta/galaxy/table';
import { catchError, combineLatest, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { getNamespace } from '../../core/services/ai-assistant-utils';
import { createConstraintFilters } from '../../core/services/constraint-utils';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../core/tokens';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { AiAssistant } from '../../core/interfaces/ai-assistant.interface';

export interface GoalInterfaceWithAssistants extends GoalInterface {
  assignedAssistants: Array<AvatarGroupItem>;
  isCustom: boolean;
}

export class AiCapabilitiesPageDataSource
  implements PaginatedAPIInterface<GoalInterfaceWithAssistants, unknown, MatSort>
{
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly goalApiService = inject(GoalApiService);
  private readonly assistantApiService = inject(AssistantApiService);
  private readonly aiAssistantService = inject(AiAssistantService);

  // Observable that fetches all assistants for the current namespace and hydrates them with decoration info
  private readonly assistants$: Observable<(AiAssistant | null)[]> = combineLatest([
    this.accountGroupId$,
    this.partnerId$,
    this.aiAssistantService.defaultWorkforce$,
  ]).pipe(
    switchMap(([accountGroupId, partnerId, defaultWorkforce]) => {
      const namespace = getNamespace(accountGroupId, partnerId);

      if (!namespace) {
        return of([]);
      }

      const assistantRequest = new ListAssistantRequest();
      const assistantFilters = new ListAssistantRequestFilters();
      assistantFilters.namespace = namespace;
      assistantRequest.filters = assistantFilters;

      return this.assistantApiService.listAssistant(assistantRequest).pipe(
        map((assistantResponse) =>
          (assistantResponse.assistants || []).map((assistant) =>
            this.aiAssistantService.hydrateAssistantWithDefaultInfo(assistant, defaultWorkforce),
          ),
        ),
        catchError((error) => {
          console.error('Error fetching assistants:', error);
          return of([]);
        }),
      );
    }),
    shareReplay(1),
  );

  get(req: PagedListRequestInterface<void, MatSort>): Observable<PagedResponseInterface<GoalInterfaceWithAssistants>> {
    const { cursor, pageSize } = req.pagingOptions ?? {};
    const { text } = req.searchOptions ?? {};
    const sortOptions = req.sorting ?? [];

    return combineLatest([this.accountGroupId$, this.partnerId$, this.assistants$]).pipe(
      switchMap(([accountGroupId, partnerId, assistants]) => {
        const namespace = getNamespace(accountGroupId, partnerId);

        if (!namespace) {
          return of({
            data: [],
            pagingMetadata: {
              nextCursor: '',
              hasMore: false,
            },
          });
        }

        // Include both the account's namespace and the global namespace to show all available capabilities
        const namespaces: Namespace[] = [namespace];
        const globalNamespace = new Namespace({ globalNamespace: {} });
        namespaces.push(globalNamespace);

        // Create a map of goal IDs to assistant objects
        const goalToAssistantsMap = new Map<string, Array<AvatarGroupItem>>();

        assistants.forEach((hydratedAssistant) => {
          hydratedAssistant?.assistant?.configurableGoals?.forEach((configurableGoal) => {
            const goalId = configurableGoal.goal?.id;
            if (goalId) {
              const currentAssistants = goalToAssistantsMap.get(goalId) || [];
              currentAssistants.push({
                src: hydratedAssistant.assistant?.avatarUrl || hydratedAssistant.decoration?.avatarIconUrl,
                name: hydratedAssistant.assistant?.name || '',
              });
              goalToAssistantsMap.set(goalId, currentAssistants);
            }
          });
        });

        // Create constraint filters based on partner/account group context
        const constraintFilters = createConstraintFilters(partnerId, accountGroupId);

        // Now fetch the capabilities
        return this.goalApiService
          .list({
            filters: {
              namespaces: namespaces,
              searchTerm: text,
              omitOverrides: true,
              sortOptions: sortOptions.map((sort) => ({
                field: sort.active,
                order:
                  sort.direction === 'asc'
                    ? ListGoalsRequestSortingOrder.GOAL_SORTING_ORDER_ASCENDING
                    : ListGoalsRequestSortingOrder.GOAL_SORTING_ORDER_DESCENDING,
              })),
              ...(constraintFilters.length > 0 && { constraintFilters }),
            },
            pagingOptions: {
              cursor,
              pageSize: pageSize || 25,
            },
          })
          .pipe(
            map((res) => {
              const capabilitiesWithAssignedAssistants = (res.goals || []).map((goal) => {
                const assignedAssistants = goalToAssistantsMap.get(goal.id || '') || [];
                const isCustom = this.isCustomCapability(goal);

                return {
                  ...goal,
                  assignedAssistants,
                  isCustom,
                };
              });

              const result = {
                data: capabilitiesWithAssignedAssistants,
                pagingMetadata: {
                  nextCursor: res.metadata?.nextCursor ?? '',
                  hasMore: res.metadata?.hasMore ?? false,
                },
              };
              return result;
            }),
            catchError((error) => {
              console.error('Error fetching AI capabilities:', error);
              // Return empty result on error
              return of({
                data: [],
                pagingMetadata: {
                  nextCursor: '',
                  hasMore: false,
                },
              });
            }),
          );
      }),
    );
  }

  /**
   * Helper method to determine namespace type from namespace interface
   */
  private getNamespaceType(namespace: any): string {
    if (!namespace) return 'local';

    if (namespace.globalNamespace) return 'global';
    if (namespace.accountGroupNamespace) return 'local';
    if (namespace.partnerNamespace) return 'local';
    if (namespace.systemNamespace) return 'system';

    return 'global'; // default fallback
  }

  /**
   * Helper method to determine if a capability is custom
   */
  private isCustomCapability(goal: GoalInterface): boolean {
    const namespaceType = this.getNamespaceType(goal.namespace);

    // Custom capabilities are local capabilities that are not managed
    return namespaceType !== 'global' && namespaceType !== 'system' && !goal.managed;
  }

  /**
   * Removes a capability from the current displayed data without refetching
   * @param capabilityId - The ID of the capability to remove
   * @param galaxyDataSource - The Galaxy data source to manipulate
   */
  removeCapability(
    capabilityId: string | undefined,
    galaxyDataSource: GalaxyDataSource<GoalInterfaceWithAssistants, unknown, unknown>,
  ): void {
    if (!capabilityId || !galaxyDataSource) {
      return;
    }

    const currentState = galaxyDataSource.state;
    const currentData = currentState.dataMembers || [];

    // Filter out the deleted capability
    const updatedData = currentData.filter((capability) => capability.id !== capabilityId);

    // Update the data source state with the filtered data
    galaxyDataSource.state = {
      ...currentState,
      dataMembers: updatedData,
      totalDataMembers:
        currentState.totalDataMembers && currentState.totalDataMembers > 0
          ? currentState.totalDataMembers - 1
          : currentState.totalDataMembers,
    };
  }
}
